import PropTypes from 'prop-types';
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import ReactModal from 'react-modal';
import VM from '@scratch/scratch-vm';
import {prewarmPaperSandbox} from '@scratch/scratch-paint';
import {injectIntl} from 'react-intl';
import intlShape from '../lib/intlShape.js';

import ErrorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {
    getIsError,
    getIsShowingProject
} from '../reducers/project-state';
import {
    activateTab,
    BLOCKS_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import {
    closeCostumeLibrary,
    closeBackdropLibrary,
    closeTelemetryModal,
    openExtensionLibrary,
    closeDebugModal
} from '../reducers/modals';

import {setPlatform} from '../reducers/platform';
import {setDynamicAssets} from '../reducers/dynamic-assets';

import FontLoaderHOC from '../lib/font-loader-hoc.jsx';
import LocalizationHOC from '../lib/localization-hoc.jsx';
import SBFileUploaderHOC from '../lib/sb-file-uploader-hoc.jsx';
import ProjectFetcherHOC from '../lib/project-fetcher-hoc.jsx';
import TitledHOC from '../lib/titled-hoc.jsx';
import ProjectSaverHOC from '../lib/project-saver-hoc.jsx';
import QueryParserHOC from '../lib/query-parser-hoc.jsx';
import ProjectFileHOC from '../lib/project-file-hoc.jsx';
import vmListenerHOC from '../lib/vm-listener-hoc.jsx';
import vmManagerHOC from '../lib/vm-manager-hoc.jsx';
import cloudManagerHOC from '../lib/cloud-manager-hoc.jsx';
import systemPreferencesHOC from '../lib/system-preferences-hoc.jsx';
import {PLATFORM} from '../lib/platform.js';

import GUIComponent from '../components/gui/gui.jsx';
import {GUIStoragePropType} from '../gui-config';
import {AccountMenuOptionsPropTypes} from '../lib/account-menu-options';
import {
    costumeShape,
    soundShape,
    spriteShape
} from '../lib/assets-prop-types.js';

class GUI extends React.Component {
    componentDidMount () {
        this.props.onStorageInit(this.props.storage.scratchStorage);
        this.props.onVmInit(this.props.vm);
        this.props.storage.setProjectMetadata?.(this.props.projectId);
        if (this.props.platform) {
            this.props.setPlatform(this.props.platform);
        }
        if (this.props.dynamicAssets) {
            this.props.onUpdateDynamicAssets(this.props.dynamicAssets);
        }
        this.maybePrewarmPaperSandbox();
    }
    componentDidUpdate (prevProps) {
        if (this.props.dynamicAssets !== prevProps.dynamicAssets) {
            this.props.onUpdateDynamicAssets(this.props.dynamicAssets);
        }
        // A session may start player-only and later enter the editor; warm the
        // paint sandbox on that transition too.
        this.maybePrewarmPaperSandbox();
        if (this.props.projectId !== prevProps.projectId) {
            if (this.props.projectId !== null) {
                this.props.onUpdateProjectId(this.props.projectId);
            }

            this.props.storage.setProjectMetadata?.(this.props.projectId);
        }
        if (this.props.isShowingProject && !prevProps.isShowingProject) {
            // this only notifies container when a project changes from not yet loaded to loaded
            // At this time the project view in www doesn't need to know when a project is unloaded
            this.props.onProjectLoaded();
        }
        if (this.props.shouldStopProject && !prevProps.shouldStopProject) {
            this.props.vm.stopAll();
        }
    }
    // Once per editor session, build and warm the Paper.js sandbox during idle
    // time so the first costume import in the paint editor is not slowed by the
    // iframe creation and Paper.js evaluation cold-start. Skipped in player-only
    // sessions, which never open the paint editor.
    maybePrewarmPaperSandbox () {
        if (this._paperSandboxWarmed || this.props.isPlayerOnly) return;
        this._paperSandboxWarmed = true;
        // Some browsers (e.g. Safari) don't support requestIdleCallback, so fall back to setTimeout.
        const scheduleIdle = window.requestIdleCallback ?
            window.requestIdleCallback.bind(window) :
            callback => setTimeout(() => callback(), 0);
        
        scheduleIdle(() => prewarmPaperSandbox());
    }
    render () {
        if (this.props.isError) {
            throw new Error(
                `Error in Scratch GUI [location=${window.location}]: ${this.props.error}`);
        }
        const {
             
            assetHost,
            cloudHost,
            error,
            isError,
            isShowingProject,
            onProjectLoaded,
            onStorageInit,
            onUpdateProjectId,
            onVmInit,
            projectHost,
            projectId,
             
            children,
            fetchingProject,
            isLoading,
            loadingStateVisible,
            projectFileLoading,
            ...componentProps
        } = this.props;


        return (
            <GUIComponent
                loading={fetchingProject || isLoading || loadingStateVisible || projectFileLoading}
                {...componentProps}
            >
                {children}
            </GUIComponent>
        );
    }
}

GUI.propTypes = {
    storage: GUIStoragePropType,
    accountMenuOptions: AccountMenuOptionsPropTypes,
    assetHost: PropTypes.string,
    children: PropTypes.node,
    cloudHost: PropTypes.string,
    dynamicAssets: PropTypes.shape({
        backdrops: PropTypes.arrayOf(costumeShape),
        costumes: PropTypes.arrayOf(costumeShape),
        sounds: PropTypes.arrayOf(soundShape),
        sprites: PropTypes.arrayOf(spriteShape)
    }),
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchingProject: PropTypes.bool,
    intl: intlShape,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isPlayerOnly: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    loadingStateVisible: PropTypes.bool,
    manuallySaveThumbnails: PropTypes.bool,
    onSetManualThumbnail: PropTypes.func,
    onSetManualThumbnailButtonClick: PropTypes.func,
    onProjectLoaded: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onStorageInit: PropTypes.func,
    onUpdateProjectId: PropTypes.func,
    onUpdateDynamicAssets: PropTypes.func,
    onVmInit: PropTypes.func,
    platform: PropTypes.oneOf(Object.keys(PLATFORM)),
    projectFileLoading: PropTypes.bool,
    setPlatform: PropTypes.func.isRequired,
    /**
     * Indicates whether we should highlight new editor features in the UI.
     * Used only when there are new features to highlight.
     */
    showNewFeatureCallouts: PropTypes.bool,
    projectHost: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shouldStopProject: PropTypes.bool,
    telemetryModalVisible: PropTypes.bool,
    username: PropTypes.string,
    avatarBadge: PropTypes.number,
    userOwnsProject: PropTypes.bool,
    // TODO: Is this unused?
    hideTutorialProjects: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};

GUI.defaultProps = {
    isTotallyNormal: false,
    onStorageInit: () => {},
    onProjectLoaded: () => {},
    onUpdateProjectId: () => {},
    onVmInit: (/* vm */) => {}
};

const mapStateToProps = (state, ownProps) => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        storage: state.scratchGui.config.storage,
        activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
        alertsVisible: state.scratchGui.alerts.visible,
        backdropLibraryVisible: state.scratchGui.modals.backdropLibrary,
        blocksTabVisible: state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
        cardsVisible: state.scratchGui.cards.visible,
        connectionModalVisible: state.scratchGui.modals.connectionModal,
        costumeLibraryVisible: state.scratchGui.modals.costumeLibrary,
        costumesTabVisible: state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
        debugModalVisible: state.scratchGui.modals.debugModal,
        error: state.scratchGui.projectState.error,
        isError: getIsError(loadingState),
        isFullScreen: state.scratchGui.mode.isFullScreen,
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        loadingStateVisible: state.scratchGui.modals.loadingProject,
        platform: ownProps.platform,
        projectId: state.scratchGui.projectState.projectId,
        soundsTabVisible: state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
        targetIsStage: (
            state.scratchGui.targets.stage &&
            state.scratchGui.targets.stage.id === state.scratchGui.targets.editingTarget
        ),
        telemetryModalVisible: state.scratchGui.modals.telemetryModal,
        tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
        vm: state.scratchGui.vm
    };
};

const mapDispatchToProps = dispatch => ({
    onExtensionButtonClick: () => dispatch(openExtensionLibrary()),
    onActivateTab: tab => dispatch(activateTab(tab)),
    onUpdateDynamicAssets: dynamicAssets => dispatch(setDynamicAssets(dynamicAssets)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    setPlatform: platform => dispatch(setPlatform(platform)),
    onRequestCloseBackdropLibrary: () => dispatch(closeBackdropLibrary()),
    onRequestCloseCostumeLibrary: () => dispatch(closeCostumeLibrary()),
    onRequestCloseDebugModal: () => dispatch(closeDebugModal()),
    onRequestCloseTelemetryModal: () => dispatch(closeTelemetryModal())
});

const ConnectedGUI = injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(GUI));

// note that redux's 'compose' function is just being used as a general utility to make
// the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
// ability to compose reducers.
const WrappedGui = compose(
    LocalizationHOC,
    ErrorBoundaryHOC('Top Level App'),
    FontLoaderHOC,
    QueryParserHOC,
    ProjectFileHOC,
    ProjectFetcherHOC,
    TitledHOC,
    ProjectSaverHOC,
    vmListenerHOC,
    vmManagerHOC,
    SBFileUploaderHOC,
    cloudManagerHOC,
    systemPreferencesHOC
)(ConnectedGUI);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
